import React from "react";
import Context from "../../../Context";
import Blocker from "../../Misc/Blocker";
import CrumbsProvider from "../../Misc/CrumbsProvider";
import MessageInput from "./MessageInput";
import MessageList from "./MessageList";
import { api } from "../../../api";

export default class Assistant extends React.Component {
  static contextType = Context;
  constructor(props) {
    super(props);
    this.state = {
      conversationId: null,
      pending: false,
      waiting: false,
      messages: [],
      conversations: [],
    };
    this.messagesEndRef = React.createRef();
    this.wsHandler = null;
  }

  componentDidMount() {
    this.loadConversations();
    this.initRouteState();
    this.renderButtons();
    this.wsHandler = this.handleWSNotification.bind(this);
    document.addEventListener("onWSNotification", this.wsHandler);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.conversationId !== this.props.conversationId) {
      this.initRouteState();
    }
    this.renderButtons();
    this.scrollToBottom();
  }

  componentWillUnmount() {
    if (this.wsHandler) {
      document.removeEventListener("onWSNotification", this.wsHandler);
    }
  }

  renderButtons = () => {
    this.context.actionsSet(<></>);
  };

  initRouteState = () => {
    const conversationId = this.props.conversationId;
    if (conversationId) {
      this.setState({ conversationId }, () => {
        this.loadMessages(conversationId);
      });
    }
  };

  handleWSNotification = (event) => {
    const detail = event.detail || {};
    const payload = detail.payload || {};
    const convId = payload.conversation || payload.conversation_id;
    if (payload.role === "assistant" && convId === this.state.conversationId) {
      this.setState((prevState) => {
        let messages = prevState.messages.filter((m) => !m.thinking);
        messages.push({
          id: payload.id || Date.now(),
          author: "Ассистент",
          right: false,
          date: new Date(payload.created || Date.now()),
          text: payload.content || "",
          sources: payload.sources || [],
        });
        return { messages, waiting: false };
      });
    }
  };

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  loadConversations = async () => {
    this.setState({ pending: true });
    let conversations = await api.qa("/ai/conversations/list");
    if (conversations && Array.isArray(conversations)) {
      this.setState({ conversations, pending: false });
    } else {
      this.setState({ conversations: [], pending: false });
    }
  };

  loadMessages = async (conversationId) => {
    if (!conversationId) return;
    this.setState({ pending: true });
    let result = await api.qa("/ai/messages", {
      conversation_id: conversationId,
    });
    if (result && Array.isArray(result)) {
      let messages = result.map((item) => ({
        id: item.id,
        author: item.role === "user" ? "Вы" : "Ассистент",
        right: item.role === "user",
        date: new Date(item.created || item.created_at || Date.now()),
        text: item.content || item.message || item.text || "",
        sources: item.sources || [],
      }));
      this.setState({ messages, pending: false });
    } else {
      this.setState({ messages: [], pending: false });
    }
  };

  onNewConversation = async () => {
    let result = await api.q("/ai/conversations", {});
    if (result && result.id) {
      this.setState({ conversationId: result.id, messages: [] });
      window.history.pushState({}, "", "/assistant/" + result.id + "/");
    }
  };

  onSend = async (message) => {
    if (!message.trim()) return;

    let { conversationId } = this.state;

    if (!conversationId) {
      let result = await api.q("/ai/conversations", {});
      if (result && result.id) {
        conversationId = result.id;
        this.setState({ conversationId });
        window.history.pushState({}, "", "/assistant/" + conversationId + "/");
      } else {
        return;
      }
    }

    let messages = this.state.messages.slice();
    messages.push({
      id: "user_" + Date.now(),
      author: "Вы",
      right: true,
      date: new Date(),
      text: message,
    });
    messages.push({
      id: "thinking",
      author: "Ассистент",
      right: false,
      date: new Date(),
      text: "Думаю...",
      thinking: true,
    });
    this.setState({ messages, waiting: true });

    let response = await api.q("/ai/ask", {
      conversation_id: conversationId,
      message: message,
      context: {},
    });

    if (response && response.message) {
      this.setState((prevState) => {
        let msgs = prevState.messages.filter((m) => !m.thinking);
        msgs.push({
          id: response.id || Date.now(),
          author: "Ассистент",
          right: false,
          date: new Date(),
          text: response.message || "",
          sources: response.sources || [],
        });
        return { messages: msgs, waiting: false };
      });
    } else if (!response) {
      // If api.q returned false, the request failed — remove thinking indicator
      this.setState((prevState) => {
        let msgs = prevState.messages.filter((m) => !m.thinking);
        return { messages: msgs, waiting: false };
      });
    }
    // If response has no message, keep waiting for WS notification
  };

  onSelectConversation = (id) => {
    this.setState({ conversationId: id }, () => {
      this.loadMessages(id);
    });
    window.history.pushState({}, "", "/assistant/" + id + "/");
  };

  onDeleteConversation = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Удалить диалог?")) return;
    let result = await api.q("/ai/conversations/delete", { id });
    if (result && result.success) {
      let conversations = this.state.conversations.filter((c) => c.id !== id);
      if (this.state.conversationId === id) {
        this.setState({ conversations, conversationId: null, messages: [] });
        window.history.pushState({}, "", "/assistant/");
      } else {
        this.setState({ conversations });
      }
    }
  };

  render() {
    return (
      <>
        <CrumbsProvider title="Ассистент" />
        <Blocker on={this.state.pending}>
          <div className="row h-100">
            <div className={`col-lg-4 no-print border-right`}>
              <div className="p-4">
                <button
                  onClick={this.onNewConversation}
                  className="btn btn-light-primary btn-block mb-4"
                >
                  + Новый диалог
                </button>
                {this.state.conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => this.onSelectConversation(conv.id)}
                    className={`p-3 mb-2 rounded cursor-pointer d-flex justify-content-between align-items-start ${
                      this.state.conversationId === conv.id
                        ? "bg-light-primary"
                        : "bg-light"
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <div>
                      <div className="font-weight-bold text-dark-75">
                        {conv.title || conv.name || "Диалог"}
                      </div>
                      {(conv.created || conv.created_at) && (
                        <div className="text-muted font-size-sm">
                          {new Date(conv.created || conv.created_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <button
                      className="btn btn-sm btn-icon btn-light-danger ml-2"
                      title="Удалить диалог"
                      onClick={(e) => this.onDeleteConversation(e, conv.id)}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="col h-100 d-flex flex-column justify-content-center">
              {this.state.conversationId ? (
                <>
                  <div className="flex-grow-1 py-3" style={{ overflowY: "auto" }}>
                    <MessageList messages={this.state.messages} />
                    <div ref={this.messagesEndRef} />
                  </div>
                  <div>
                    <MessageInput
                      onSend={this.onSend}
                      disabled={this.state.waiting}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="h4 text-center text-muted">
                    Начните новый диалог или выберите существующий
                  </div>
                </>
              )}
            </div>
          </div>
        </Blocker>
      </>
    );
  }
}
